/**
 * Header behavior: sticky-on-scroll, transparent-over-hero logo swap, and
 * mobile off-canvas menu toggle.
 *
 * Reads data attributes set by header.php (craftis_get_header_layout(),
 * craftis_header_is_transparent(), the Header tab's sticky/mobile-style
 * fields) rather than hardcoding behavior, so the same script drives all
 * three header layout partials.
 */

const SCROLL_THRESHOLD = 40;

function initHeader() {
	const header = document.querySelector( '[data-craftis-header]' );
	if ( ! header ) {
		return;
	}

	const isSticky = header.dataset.sticky === '1';
	const isTransparent = header.dataset.transparent === '1';

	if ( isSticky || isTransparent ) {
		const onScroll = () => {
			const scrolled = window.scrollY > SCROLL_THRESHOLD;
			header.classList.toggle( 'craftis-header--scrolled', scrolled );
		};

		window.addEventListener( 'scroll', onScroll, { passive: true } );
		onScroll();
	}

	initOffCanvas( header );
	initNavPill( header );
	initCartDropdown( header );
	initSearchToggle( header );
	initMobileSearchOverlay( header );
}

// Matches $bp-sm in assets/src/scss/_variables.scss — below this width the
// pill's inline search form is display:none (no room for it in the pill),
// so the trigger opens the full-screen overlay instead. Kept as a
// matchMedia call (not a JS width constant compared to innerWidth) so it
// stays in sync with the CSS breakpoint even if the browser zoom level or
// devtools device toolbar changes the effective viewport mid-session.
const MOBILE_SEARCH_QUERY = '(max-width: 767px)';

/**
 * Search toggle inside the nav pill: clicking the search icon hides the
 * nav menu and reveals a search input in the exact same spot within
 * .craftis-header__pill (both live stacked in .craftis-header__pill-center,
 * see _header.scss), rather than opening a separate overlay. Toggled via
 * a single .is-search-open class on the pill itself.
 *
 * Only runs the pill-toggle behavior at bp-up($bp-sm) — below that, the
 * pill's search form is hidden entirely (no room for it) and
 * initMobileSearchOverlay() below handles the same trigger click instead.
 *
 * @param {HTMLElement} header Root .craftis-header element.
 */
function initSearchToggle( header ) {
	const pill = header.querySelector( '[data-craftis-pill]' );
	const trigger = header.querySelector( '[data-craftis-search-trigger]' );
	const form = header.querySelector( '[data-craftis-search-form]' );
	const input = header.querySelector( '[data-craftis-search-input]' );
	const cancelBtn = header.querySelector( '[data-craftis-search-cancel]' );

	if ( ! pill || ! trigger || ! form ) {
		return;
	}

	// Matches the clip-path transition duration in _header.scss — focusing
	// the input only once the slide-open animation has actually finished
	// avoids the mobile keyboard shoving the still-animating layout around.
	const OPEN_TRANSITION_MS = 350;

	function open() {
		pill.classList.add( 'is-search-open' );
		trigger.setAttribute( 'aria-expanded', 'true' );
		if ( input ) {
			window.setTimeout( () => input.focus(), OPEN_TRANSITION_MS );
		}
	}

	function close() {
		pill.classList.remove( 'is-search-open' );
		trigger.setAttribute( 'aria-expanded', 'false' );
		if ( input ) {
			input.blur();
		}
	}

	trigger.addEventListener( 'click', ( event ) => {
		if ( window.matchMedia( MOBILE_SEARCH_QUERY ).matches ) {
			return;
		}

		event.stopPropagation();
		pill.classList.contains( 'is-search-open' ) ? close() : open();
	} );

	if ( cancelBtn ) {
		cancelBtn.addEventListener( 'click', ( event ) => {
			event.stopPropagation();
			close();
		} );
	}

	document.addEventListener( 'click', ( event ) => {
		if ( pill.classList.contains( 'is-search-open' ) && ! form.contains( event.target ) && event.target !== trigger ) {
			close();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' && pill.classList.contains( 'is-search-open' ) ) {
			close();
		}
	} );
}

/**
 * Full-screen mobile search overlay (.craftis-search-overlay, printed once
 * in template-parts/header/nav-mobile.php — a page-level singleton, same
 * as the off-canvas menu it sits beside). Below $bp-sm, the same header
 * search trigger this widget shares with initSearchToggle() opens this
 * overlay instead of the (hidden, no-room) pill form.
 *
 * @param {HTMLElement} header Root .craftis-header element.
 */
function initMobileSearchOverlay( header ) {
	const trigger = header.querySelector( '[data-craftis-search-trigger]' );
	const overlay = document.querySelector( '[data-craftis-search-overlay]' );
	const closeBtn = overlay ? overlay.querySelector( '[data-craftis-search-overlay-close]' ) : null;
	const input = overlay ? overlay.querySelector( '[data-craftis-search-overlay-input]' ) : null;

	if ( ! trigger || ! overlay ) {
		return;
	}

	// Matches .craftis-search-overlay's transition-duration in _header.scss —
	// focusing only once the slide-in has finished keeps the mobile
	// keyboard from shoving the still-animating layout around.
	const OPEN_TRANSITION_MS = 300;

	function open() {
		overlay.classList.add( 'is-open' );
		overlay.setAttribute( 'aria-hidden', 'false' );
		trigger.setAttribute( 'aria-expanded', 'true' );
		document.body.style.overflow = 'hidden';

		if ( input ) {
			window.setTimeout( () => input.focus(), OPEN_TRANSITION_MS );
		}
	}

	function close() {
		overlay.classList.remove( 'is-open' );
		overlay.setAttribute( 'aria-hidden', 'true' );
		trigger.setAttribute( 'aria-expanded', 'false' );
		document.body.style.overflow = '';

		if ( input ) {
			input.blur();
		}
	}

	trigger.addEventListener( 'click', ( event ) => {
		if ( ! window.matchMedia( MOBILE_SEARCH_QUERY ).matches ) {
			return;
		}

		event.stopPropagation();
		overlay.classList.contains( 'is-open' ) ? close() : open();
	} );

	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', close );
	}

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' && overlay.classList.contains( 'is-open' ) ) {
			close();
		}
	} );

	// A resize past the mobile breakpoint (e.g. rotating a tablet, or
	// widening a resizable devtools viewport) while the overlay is open
	// would otherwise leave it stuck open under the desktop pill layout.
	window.addEventListener( 'resize', () => {
		if ( overlay.classList.contains( 'is-open' ) && ! window.matchMedia( MOBILE_SEARCH_QUERY ).matches ) {
			close();
		}
	} );
}

/**
 * Mini-cart dropdown: toggles open/closed on clicking the header cart
 * icon, closes on outside click, Escape, or via WooCommerce's own
 * "added_to_cart" event (so adding a product from anywhere on the site
 * opens the dropdown to show what was just added).
 *
 * @param {HTMLElement} header Root .craftis-header element.
 */
function initCartDropdown( header ) {
	const trigger = header.querySelector( '[data-craftis-cart-trigger]' );
	const wrapper = trigger ? trigger.closest( '.craftis-cart-dropdown' ) : null;
	const panel = wrapper ? wrapper.querySelector( '[data-craftis-cart-panel]' ) : null;

	if ( ! trigger || ! panel ) {
		return;
	}

	function open() {
		panel.classList.add( 'is-open' );
		panel.setAttribute( 'aria-hidden', 'false' );
		trigger.setAttribute( 'aria-expanded', 'true' );
	}

	function close() {
		panel.classList.remove( 'is-open' );
		panel.setAttribute( 'aria-hidden', 'true' );
		trigger.setAttribute( 'aria-expanded', 'false' );
	}

	trigger.addEventListener( 'click', ( event ) => {
		event.stopPropagation();
		panel.classList.contains( 'is-open' ) ? close() : open();
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( panel.classList.contains( 'is-open' ) && ! wrapper.contains( event.target ) ) {
			close();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' ) {
			close();
		}
	} );

	// WooCommerce fires this on jQuery('body') after any AJAX
	// add-to-cart action, whether the WC AJAX add-to-cart button was used
	// or a fragment-refreshing form was submitted.
	if ( window.jQuery ) {
		window.jQuery( document.body ).on( 'added_to_cart', open );
	}

	initCartDropdownRemove( panel );
}

/**
 * Intercepts clicks on a mini-cart line item's remove (&times;) link so it
 * removes the item via WooCommerce's own wc-ajax=remove_from_cart endpoint
 * instead of following the href, which would otherwise navigate to
 * ?remove_item=... and reload the page. On success, the panel (and the
 * header cart count) are refreshed in place from the same fragments
 * craftis_cart_fragments() already registers for add-to-cart — falls back
 * to a normal navigation if the request itself fails.
 *
 * @param {HTMLElement} panel The mini-cart panel ([data-craftis-cart-panel]).
 */
function initCartDropdownRemove( panel ) {
	panel.addEventListener( 'click', ( event ) => {
		const link = event.target.closest( '[data-craftis-cart-remove]' );
		if ( ! link || ! panel.contains( link ) ) {
			return;
		}

		if ( ! window.craftisCartL10n || ! window.craftisCartL10n.removeUrl ) {
			return;
		}

		event.preventDefault();

		const cartItemKey = link.dataset.cartItemKey;
		if ( ! cartItemKey ) {
			return;
		}

		const body = new URLSearchParams( { cart_item_key: cartItemKey } );

		fetch( window.craftisCartL10n.removeUrl, {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: body.toString(),
		} )
			.then( ( response ) => response.json() )
			.then( ( json ) => {
				if ( ! json || ! json.success || ! json.data || ! json.data.fragments ) {
					window.location.href = link.href;
					return;
				}

				Object.keys( json.data.fragments ).forEach( ( selector ) => {
					document.querySelectorAll( selector ).forEach( ( el ) => {
						// The panel itself is swapped in place (innerHTML,
						// keeping the same node) rather than outerHTML, since
						// this click listener is bound to that exact node —
						// replacing it outright would silently drop the
						// listener for every remove-click after the first.
						if ( el === panel ) {
							const wasOpen = panel.classList.contains( 'is-open' );
							const temp = document.createElement( 'div' );
							temp.innerHTML = json.data.fragments[ selector ];
							const newPanel = temp.firstElementChild;
							panel.innerHTML = newPanel ? newPanel.innerHTML : '';
							panel.classList.toggle( 'is-open', wasOpen );
							return;
						}

						el.outerHTML = json.data.fragments[ selector ];
					} );
				} );

				if ( window.jQuery ) {
					window.jQuery( document.body ).trigger( 'wc_fragments_refreshed' );
				}
			} )
			.catch( () => {
				window.location.href = link.href;
			} );
	} );
}

/**
 * Sliding pill indicator for the primary nav: a single absolutely-
 * positioned span, inserted once into .craftis-nav__list, moved via
 * inline transform/width to sit under whichever link is hovered, and
 * reset to the current page's link (WordPress's own
 * .current-menu-item/.current_page_item classes) when the mouse leaves
 * the nav entirely.
 *
 * @param {HTMLElement} header Root .craftis-header element.
 */
function initNavPill( header ) {
	const list = header.querySelector( '.craftis-nav__list' );
	if ( ! list ) {
		return;
	}

	const indicator = document.createElement( 'span' );
	indicator.className = 'craftis-nav__pill-indicator';
	indicator.setAttribute( 'aria-hidden', 'true' );
	list.appendChild( indicator );

	const items = Array.from( list.querySelectorAll( ':scope > li' ) );
	if ( items.length === 0 ) {
		return;
	}

	const activeItem = list.querySelector( '.current-menu-item, .current_page_item' );

	const PILL_PADDING_X = 20;

	function moveIndicatorTo( item ) {
		// The indicator is now a solid-filled pill (not an outline), so
		// whichever item it currently sits behind needs its own text to
		// flip to a light color for contrast — .craftis-nav__pill-active
		// tracks that on the <li>, cleared from every item first so only
		// the current one ever carries it.
		items.forEach( ( li ) => li.classList.remove( 'craftis-nav__pill-active' ) );

		if ( ! item ) {
			indicator.classList.remove( 'is-visible' );
			return;
		}

		const listRect = list.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();

		indicator.style.width = `${ itemRect.width + PILL_PADDING_X * 2 }px`;
		indicator.style.transform = `translate(${ itemRect.left - listRect.left - PILL_PADDING_X }px, -50%)`;
		indicator.classList.add( 'is-visible' );
		item.classList.add( 'craftis-nav__pill-active' );
	}

	items.forEach( ( item ) => {
		item.addEventListener( 'mouseenter', () => moveIndicatorTo( item ) );
	} );

	list.addEventListener( 'mouseleave', () => moveIndicatorTo( activeItem ) );

	// Initial position: show on the active page's link if there is one,
	// with its transition disabled first — every click on a nav link is
	// a full page navigation, so this code re-runs from scratch on every
	// load. Without this, the pill would visibly slide in from the left
	// (its CSS default position) on every single page load, not just
	// animate between hovers within one page view as intended.
	if ( activeItem ) {
		indicator.classList.add( 'is-positioning' );
		moveIndicatorTo( activeItem );

		// Force layout so the instant (transition-disabled) position is
		// actually painted before transitions are re-enabled on the next
		// frame — otherwise the browser can coalesce both style changes
		// into one paint and the transition would still apply.
		indicator.getBoundingClientRect();

		requestAnimationFrame( () => {
			indicator.classList.remove( 'is-positioning' );
		} );
	}

	// Re-measure on resize, since link widths/positions can shift under a
	// layout change (e.g. rotating a device, resizing the browser).
	window.addEventListener( 'resize', () => moveIndicatorTo( activeItem ) );
}

function initOffCanvas( header ) {
	const trigger = header.querySelector( '[data-craftis-menu-trigger]' );
	const panel = document.querySelector( '[data-craftis-offcanvas]' );
	const overlay = document.querySelector( '[data-craftis-offcanvas-overlay]' );
	const closeBtn = panel ? panel.querySelector( '[data-craftis-offcanvas-close]' ) : null;

	if ( ! trigger || ! panel || ! overlay ) {
		return;
	}

	const open = () => {
		panel.classList.add( 'is-open' );
		overlay.classList.add( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'true' );
		document.body.style.overflow = 'hidden';
	};

	const close = () => {
		panel.classList.remove( 'is-open' );
		overlay.classList.remove( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'false' );
		document.body.style.overflow = '';
	};

	trigger.addEventListener( 'click', () => {
		const isOpen = panel.classList.contains( 'is-open' );
		isOpen ? close() : open();
	} );

	overlay.addEventListener( 'click', close );
	if ( closeBtn ) {
		closeBtn.addEventListener( 'click', close );
	}

	document.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' ) {
			close();
		}
	} );
}

document.addEventListener( 'DOMContentLoaded', initHeader );
